import { useAsync } from '@tiger/hooks';
import { Button, Tooltip } from 'antd';
import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Toast from 'app/components/Toast';
import Modal, { useConfirmModal } from 'components/Modal';
import Skeleton from 'components/Skeleton';
import Track from 'components/Track';
import { SECURITY } from 'stores/menu';
import security from 'stores/security';
import { printf } from 'utils/i18n';
import require2FA from 'utils/require2FA';
import store from '../../store';
import PositionSwitch from './PositionSwitch';
import './style.scss';

const Security: React.FC = observer(() => {
    const navigate = useNavigate();
    const { open: openConfirmModal } = useConfirmModal();
    const [trustState] = useAsync(() => store.getTrust());
    const [marketingState, getMarketingState] = useAsync(() => store.getMarketingSetting());
    const [ibNameState] = useAsync(() => store.getIBName());
    let otp_code = '';

    // 关闭TradeUP Authenticator
    const optCodeClick = () => {
        if (store.otp_enable) {
            openConfirmModal({
                title: __('Disable 2-step Verification'),
                content: __(
                    'After disabling 2-step verification, your account may be exposed to more risks. Are you sure you want to disable 2-step verification?'
                ),
                onOk: () => {
                    navigate('2fa/uninstall/');
                },
                cancelText: __('取消'),
                okText: __('确认')
            });
        } else {
            navigate('2fa/install');
        }
    };

    const activeEmail = () => {
        navigate('active/email/1');
    };

    const modifyEmail = () => {
        navigate('modify/email/1');
    };

    const setEmail = () => {
        navigate('bind/email/1');
    };

    const onPhoneClick = () => {
        if (!security.userInfo?.phone) {
            navigate('bind/phone/1');
        } else {
            navigate('modify/phone/1');
        }
    };

    const change2FAPosition = async verify => {
        try {
            const params: any = {
                verify
            };

            if (otp_code) {
                params.otp_code = otp_code;
            }

            const ret = await store.setPosition(params);

            if (ret.is_succ) {
                Toast.show(__('两步验证位置设置成功'));
            } else {
                Toast.show(__('两步验证位置设置失败'));
            }
        } catch (error) {
            let showToast = true;

            const { error_code, error_msg } = error;

            // 需要两步验证token
            if (error_code >= 11401 && error_code <= 11402) {
                showToast = false;

                if (error_code === 11402) {
                    showToast = true;
                }

                require2FA()
                    .then(code => {
                        otp_code = code;
                        change2FAPosition(verify);
                    })
                    .catch(e => {
                        Toast.show(e.message);
                    });
            }

            if (showToast) {
                Toast.show(error_msg || error.message);
            }
        }
    };

    // 调整TradeUP Authenticator位置
    const optCodePosition = () => {
        Modal.open({
            component: <PositionSwitch />,
            className: 'switch-2fa-modal',
            title: __('开启位置'),
            width: 360,
            closable: true
        }).result.then(result => {
            change2FAPosition(result);
        });
    };

    // 取消/开启邮件订阅
    const toggleMarketingMail = async () => {
        try {
            Toast.loading(true);
            await store.updateMarketingSetting(!marketingState.data!.enabledSend);
            Toast.loading(false);

            await Toast.show(
                marketingState.data!.enabledSend ? __('Unsubscribed Successfully') : __('Subscribed Successfully')
            );

            // 重新获取最新的数据
            getMarketingState();
        } catch (err) {
            Toast.show(err);
        }
    };

    function render() {
        const { userInfo: { loginPassword, phone, emailActive, email, telCode } = {}, currentProcessStatusCode } =
            security;
        const passwordExist = !!loginPassword,
            phoneExist = !!phone,
            emailExist = !!email,
            otpEnabled = store.otp_enable;

        return (
            <>
                <h1>{SECURITY}</h1>
                <div className="security-list">
                    <div className="security-item">
                        <div className="left">
                            <h3>{__('手机认证')}</h3>
                            <span>{__('找回密码、获取消息通知时使用')}</span>
                        </div>
                        <div className="right">
                            <span className="status">
                                <Tooltip title={phoneExist ? __('已认证') : __('未认证')} trigger="click">
                                    <span className={phoneExist ? 'checked' : 'unchecked'}>
                                        <span className="desc">{phoneExist ? __('已认证') : __('未认证')}</span>
                                    </span>
                                </Tooltip>
                            </span>
                            <span className="large-visible">{phoneExist ? `+${telCode} ${phone}` : <>&nbsp;</>}</span>
                            <div className="handlers">
                                <Track
                                    eventModel="安全中心"
                                    eventType="手机"
                                    eventName={phoneExist ? __('修改') : __('去认证')}>
                                    <Button onClick={onPhoneClick} type="primary" size="small">
                                        {phoneExist ? __('修改') : __('去认证')}
                                    </Button>
                                </Track>
                            </div>
                        </div>
                    </div>

                    <div className="security-item">
                        <div className="left">
                            <h3>{__('邮箱认证')}</h3>
                            <span>{__('找回密码、获取消息通知时使用')}</span>
                        </div>
                        <div className="right">
                            <span className="status">
                                <Tooltip
                                    title={!emailExist ? __('未设置') : emailActive ? __('已认证') : __('未认证')}
                                    trigger="click">
                                    <span className={emailActive ? 'checked' : 'unchecked'}>
                                        <span className="desc">
                                            {!emailExist ? __('未设置') : emailActive ? __('已认证') : __('未认证')}
                                        </span>
                                    </span>
                                </Tooltip>
                            </span>
                            <span className="large-visible">{email}</span>
                            <div className="handlers">
                                {emailExist ? (
                                    <Track eventModel="安全中心" eventType="邮箱" eventName="修改">
                                        <Button type="primary" size="small" onClick={modifyEmail}>
                                            {__('修改')}
                                        </Button>
                                    </Track>
                                ) : (
                                    <Track eventModel="安全中心" eventType="邮箱" eventName="去设置">
                                        <Button onClick={setEmail} type="primary" size="small">
                                            {__('去设置')}
                                        </Button>
                                    </Track>
                                )}
                                {emailExist && !emailActive && (
                                    <Track eventModel="安全中心" eventType="邮箱" eventName="去认证">
                                        <Button type="primary" size="small" onClick={activeEmail}>
                                            {__('AMS-email:去认证')}
                                        </Button>
                                    </Track>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="security-item">
                        <div className="left">
                            <h3>{__('登录密码')}</h3>
                            <span>{__('登录TradeUP APP及官方网站时使用')}</span>
                        </div>
                        <div className="right">
                            <span className="desc">
                                <Tooltip title={passwordExist ? __('已设置') : __('未设置')} trigger="click">
                                    <span className={passwordExist ? 'checked' : 'unchecked'}>
                                        <span className="desc">{passwordExist ? __('已设置') : __('未设置')}</span>
                                    </span>
                                </Tooltip>
                            </span>
                            <span className="large-visible">&nbsp;</span>
                            <div className="handlers">
                                <Track
                                    eventModel="安全中心"
                                    eventType={passwordExist ? '登录密码_修改' : '登录密码_去设置'}
                                    eventName={passwordExist ? __('修改') : __('设置')}>
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={() => navigate(passwordExist ? 'modify/password/1' : 'set/password')}>
                                        {passwordExist ? __('修改') : __('设置')}
                                    </Button>
                                </Track>
                            </div>
                        </div>
                    </div>
                    {/* 已开户或已入金 */}
                    {currentProcessStatusCode >= 3 && (
                        <>
                            <div className="security-item">
                                <div className="left">
                                    <h3>{__('两步验证')}</h3>
                                    <span>{__('提高安全性、敏感操作时验证动态密码')}</span>
                                </div>
                                <div className="right">
                                    <span className="status">
                                        <Tooltip title={otpEnabled ? __('已开启') : __('未开启')} trigger="click">
                                            <span className={otpEnabled ? 'checked' : 'unchecked'}>
                                                <span className="desc">{otpEnabled ? __('已开启') : __('未开启')}</span>
                                            </span>
                                        </Tooltip>
                                    </span>
                                    <span className="large-visible">&nbsp;</span>
                                    <div className="handlers">
                                        {otpEnabled && (
                                            <Track eventModel="安全中心" eventType="位置" eventName="设置">
                                                <Button type="primary" size="small" onClick={optCodePosition}>
                                                    {__('位置')}
                                                </Button>
                                            </Track>
                                        )}
                                        <Track
                                            eventModel="安全中心"
                                            eventType="位置"
                                            eventName={otpEnabled ? __('关闭') : __('开启')}>
                                            <Button type="primary" size="small" onClick={optCodeClick}>
                                                {otpEnabled ? __('关闭') : __('开启')}
                                            </Button>
                                        </Track>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    {!security.isMarscoAccount &&
                        (ibNameState.error ? null : ibNameState.loading ? (
                            <div className="security-item-skeleton">
                                <Skeleton line={2} />
                            </div>
                        ) : (
                            <div className="security-item">
                                <div className="left">
                                    <h3>{__('Client Portal Login Credentials')}</h3>
                                    <span>{__('Client Portal Login Credentials')}</span>
                                </div>
                                <div className="right">
                                    <span className="status">
                                        <span className="checked checked-pad">
                                            <span className="desc">{__('User Name')}</span>
                                        </span>
                                    </span>
                                    <span className="large-visible">{ibNameState.data?.ib_username || '--'}</span>
                                    <div className="handlers">
                                        <Track
                                            eventModel="安全中心"
                                            eventType={'Client Portal Login Credentials_去设置'}
                                            eventName={__('设置')}>
                                            <Button
                                                type="primary"
                                                size="small"
                                                onClick={() => navigate('link')}
                                                disabled={
                                                    !['FUNDED', 'OPENED'].includes(security.currentProcessStatus)
                                                }>
                                                {['FUNDED', 'OPENED'].includes(security.currentProcessStatus)
                                                    ? __('Retrieve')
                                                    : __('Not Available')}
                                            </Button>
                                        </Track>
                                    </div>
                                </div>
                            </div>
                        ))}
                    {trustState.error ? null : trustState.loading ? (
                        <div className="security-item-skeleton">
                            <Skeleton line={2} />
                        </div>
                    ) : (
                        <div className="security-item">
                            <div className="left">
                                <h3>{__('Trusted Contact Person')}</h3>
                                <span>{__('Trusted Contact Person')}</span>
                            </div>
                            <div className="right">
                                <span className="status">
                                    <Tooltip
                                        title={trustState.data?.total ? __('已设置') : __('未设置')}
                                        trigger="click">
                                        <span className={trustState.data?.total ? 'checked' : 'unchecked'}>
                                            <span className="desc">
                                                {trustState.data?.total ? __('已设置') : __('未设置')}
                                            </span>
                                        </span>
                                    </Tooltip>
                                </span>
                                <span className="large-visible">
                                    {trustState.data?.total
                                        ? printf(__('Registered %s person'), trustState.data.total)
                                        : ' '}
                                </span>
                                <div className="handlers">
                                    <Track
                                        eventModel="安全中心"
                                        eventType={'Trusted Contact Person_去设置'}
                                        eventName={__('设置')}>
                                        <Button type="primary" size="small" onClick={() => navigate('trust')}>
                                            {__('设置')}
                                        </Button>
                                    </Track>
                                </div>
                            </div>
                        </div>
                    )}
                    {marketingState.error ? null : marketingState.loading ? (
                        <div className="security-item-skeleton">
                            <Skeleton line={2} />
                        </div>
                    ) : (
                        <div className="security-item">
                            <div className="left">
                                <h3>{__('Marketing Mail')}</h3>
                                <span>{__('Marketing Mail')}</span>
                            </div>
                            <div className="right">
                                <span className="desc">
                                    <Tooltip
                                        title={marketingState.data?.enabledSend ? __('Subscribed') : __('Unsubscribed')}
                                        trigger="click">
                                        <span className={marketingState.data?.enabledSend ? 'checked' : 'unchecked'}>
                                            <span className="desc">
                                                {marketingState.data?.enabledSend
                                                    ? __('Subscribed')
                                                    : __('Unsubscribed')}
                                            </span>
                                        </span>
                                    </Tooltip>
                                </span>
                                <span className="large-visible">&nbsp;</span>
                                <div className="handlers">
                                    <Track
                                        eventModel="安全中心"
                                        eventType={'Marketing Mail_去设置'}
                                        eventName={__('设置')}>
                                        <Button type="primary" size="small" onClick={toggleMarketingMail}>
                                            {marketingState.data?.enabledSend ? __('Unsubscribe') : __('Subscribe')}
                                        </Button>
                                    </Track>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </>
        );
    }

    return <div className="security-page">{render()}</div>;
});

export default Security;
